//Imports
import React, { Fragment, useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeUnits } from 'Core/hooks';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useIntl } from "react-intl";
import moment from 'moment';

//Actions
import { sendGenericExport, clearRedux, openModalCreateEditWorkOrder, openModalDeleteWorkOrder, savePreference, openModalChangeStatusWorkOrder, openViewAnswersModal, openOverViewUnit } from "Redux/actions";
import { openModalTrackerAdvancedFilters } from './modals/advancedFilters/redux/slice'
import { reset, searchWorkOrderGrouped } from "./redux/slice";

//Controls
import {
    Container,
    IconButton,
    MenuComponent,
    SearchInput,
    TableButtons,
    TableView,
    useTablePagination,
    Pill
} from "@gpstrackit/gtc-react-controls";
import { Grid, Icon, Typography } from "@mui/material";
import { Tooltip } from 'Components';
import MapModal from "Modules/.common/components/MapModal";
import { Avatar, LinearProgress } from 'Components';

//Helpers
import { getInitialRange, completeColumns, splitTextIntoLines, GetFormatForMoment } from "Core/data/Helpers";
import { getAbbvTimezone } from "Components/uiControls/Timezone/tools";

//Components
import { NotificationManager } from "../../../../components/Notifications";
import Preferences from "Core/containers/Preferences";

//Modals
import CreateEditWorkOrder from './modals/createEditWorkOrder';
import AdvancedFilters from './modals/advancedFilters';
import DeleteWorkOrder from './modals/deleteWorkOrder';
import ChangeStatus from './modals/changeStatus';
import ViewAnswers from '../../../dynamicForms/modals/viewAnswers';

//Styles
import './index.scss';

//Variables
const KEY_PREFERENCE = "WorkOrderMain-list";
const INITIAL_STATE = {
    rangeDate: {
        start: moment().startOf('month'),
        end: moment().endOf('month')
    },
    timeFrame: 'thisMonth',
    tags: [],
    drivers: [],
    customers: [],
    status: [],
    types: [],
    severities: []
}

const TrackerTab = () => {

    const dispatch = useDispatch();

    //Language
    const { messages, locale } = useIntl();

    const severityList = useMemo(() => [
        { id: "Low", name: messages['workOrder_createEdit_low'], color: "#66bb6a" },
        { id: "Medium", name: messages['workOrder_createEdit_medium'], color: "#FFB53E" },
        { id: "High", name: messages['workOrder_createEdit_high'], color: "#df1a31" }
    ], [messages]);

    //States
    const [selectedItems, setSelectedItems] = useState([]);
    const [openSearch, setOpenSearch] = useState(null);
    const [formData, setFormData] = useState(INITIAL_STATE);
    const [preferences, setPreferences] = useState({});
    const [loadPreference, setLoadPreferences] = useState(false);
    const [loadColumns, setLoadColumns] = useState(false);
    const [dataToMap, setDataToMap] = useState({});
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

    //Table refs
    const trackerTableRef = useRef();
    const typingTimeRef = useRef(null);

    //Redux States
    const { loadingWorkOrders, workOrders, totalWorkOrders, error, workOrdersGrouped, totalWorkOrdersGrouped, loadingWorkOrdersGrouped } = useSelector((state) => state.workOrdersTrackerTabRedux);
    const openModalFilters = useSelector((state) => state.workOrdersTrackerTabAdvancedFiltersRedux.open);
    const openModalCreateEdit = useSelector((state) => state.workOrdersCreateEditWorkOrderRedux.open);
    const openModalDelete = useSelector((state) => state.workOrdersDeleteWorkOrderRedux?.open);
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);
    const user = useSelector((state) => state.securityRedux.user);
    const openModalChangeStatus = useSelector((state) => state.workOrdersChangeStatusRedux.open);
    const openModalViewAnswers = useSelector(state => state.dynamicFormsViewAnswersRedux.open);
    const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
    const [viewSelected, setViewSelected] = useState("day");
    const { dateformat, timeZone } = user;


    // Retry loading preferences if they failed to load initially due to missing dependencies
    useEffect(() => {
        if (!loadPreference && !isInitialLoadComplete && timeZone && user && preferences && Object.keys(preferences).length > 0) {
            // Handle migration from old "columns" to new separated column preferences
            let migrationPreferences = { ...preferences };
            if (preferences?.columns && !preferences?.columnsDay && !preferences?.columnsDriver) {
                // Migrate old columns to both views (they'll be customized separately from now on)
                migrationPreferences.columnsDay = preferences.columns;
                migrationPreferences.columnsDriver = preferences.columns;
                // Remove old columns key
                delete migrationPreferences.columns;
            }

            // Preferences exist but weren't applied, retry now that dependencies are available
            let updatedFormData = {
                ...formData, ...['severities', 'status', 'types', 'rangeDate', 'timeFrame', 'drivers', 'customers', 'tags'].reduce((acc, key) => {
                    if (migrationPreferences?.[key]) acc[key] = migrationPreferences[key];
                    return acc;
                }, {})
            };
            if (migrationPreferences?.timeFrame) {
                const timeFrame = getInitialRange(migrationPreferences.timeFrame, migrationPreferences?.rangeDate?.start, migrationPreferences?.rangeDate?.end);
                if (timeFrame?.start && timeFrame?.end) {
                    updatedFormData = {
                        ...updatedFormData,
                        rangeDate: timeFrame,
                        timeFrame: migrationPreferences.timeFrame
                    }
                }
            }

            setFormData(updatedFormData);
            setPreferences(migrationPreferences);
            setLoadPreferences(true);

            // Apply the filters
            setTimeout(() => {
                const currentPagination = viewSelected === "day" ? paginationTrackerDay : paginationTrackerDriver;
                const currentConditions = buildConditions(updatedFormData, viewSelected, timeZone);
                currentPagination.refresh({ _conditions: currentConditions });
                setIsInitialLoadComplete(true);
            }, 100);
        }
    }, [timeZone, user, loadPreference]);

    const modelOverView = useCallback((id) => {
        const item = units.find((item) => item.id == id)
        dispatch(
            openOverViewUnit({
                data: { unit: item },
                open: true,
            }))
    }, [units, dispatch])

    //Format
    const format = GetFormatForMoment(dateformat);

    const paginationTrackerDay = useTablePagination({
        dispatch,
        action: searchWorkOrderGrouped,
        searchFields: ["id", "customerId", "workOrderNumber"],
        tableRef: trackerTableRef,
        sendGenericExport,
        exportName: "tracker-day",
    });

    const paginationTrackerDriver = useTablePagination({
        dispatch,
        action: searchWorkOrderGrouped,
        searchFields: ["id", "customerId", "workOrderNumber"],
        tableRef: trackerTableRef,
        sendGenericExport,
        exportName: "tracker-driver",
    });

    // Get current pagination based on selected view
    const paginationTracker = viewSelected === "day" ? paginationTrackerDay : paginationTrackerDriver;

    useEffect(() => {
        if (loadPreference && timeZone && isInitialLoadComplete) {
            setSelectedItems([]);
            trackerTableRef?.current?.setSelectedItems([]);

            const currentPagination = viewSelected === "day" ? paginationTrackerDay : paginationTrackerDriver;
            currentPagination.refresh({
                _page: 1,
                _pageSize: 10,
                _conditions: buildConditions(formData, viewSelected, timeZone),
            });
        }

    }, [viewSelected]);

    const refreshTable = (isForceRefreshMemcached = false) => {
        formData.forceRefreshMemcached = isForceRefreshMemcached;
        setSelectedItems([]);
        trackerTableRef?.current?.setPage(1);
        trackerTableRef?.current?.setSelectedItems([]);
        setTimeout(() => {
            paginationTracker.refresh({
                _page: 1,
                _pageSize: 10,
                _conditions: buildConditions(formData, viewSelected, timeZone),
            });
        }, 100);
        applyChanges('_page', 1)

    };

    const savePreferences = (preferencesArray) => {
        if (!Array.isArray(preferencesArray)) {
            console.error("Expected an array of preferences");
            return;
        }

        let updatedPreferences = { ...preferences } || {};

        preferencesArray.forEach(({ key, value }) => {
            updatedPreferences[key] = value;
        });

        setPreferences(updatedPreferences);

        dispatch(savePreference({
            entityName: "User",
            entityId: authenticatedId,
            preferenceKey: KEY_PREFERENCE,
            preferenceValue: updatedPreferences
        }));
    };

    const applyChanges = (field, value) => {
        const updatedParams = {
            [field]: value,
            _conditions: buildConditions(formData, viewSelected, timeZone),
        };
        if (field === '_pageSize') {
            updatedParams._page = 1;
        }
        if (field === '_searchText') {
            setOpenSearch(value);

            if (value === '') {
                updatedParams._conditions = buildConditions({ ...formData, forceRefreshMemcached: true }, viewSelected, timeZone)
            }
            if (typingTimeRef.current) {
                clearTimeout(typingTimeRef.current);
            }
            typingTimeRef.current = setTimeout(() => {
                paginationTracker.refresh(updatedParams);
            }, 1000);
        } else {
            paginationTracker.refresh(updatedParams);
        }

    }

    const hasFilters = useCallback(() => {
        const excludedKeys = ["rangeDate"];
        const defaultValues = {
            timeFrame: "thisMonth",
            tags: [],
            drivers: [],
            customers: [],
            status: [],
            types: [],
            severities: []
        };

        const isArrayEqual = (arr1, arr2) => {
            if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
            return arr1.length === arr2.length && arr1.every((val, index) => val === arr2[index]);
        };

        return Object.keys(formData).some(key => {
            if (excludedKeys.includes(key)) return false;

            const formValue = formData[key];
            const defaultValue = defaultValues[key];

            if (Array.isArray(formValue) && Array.isArray(defaultValue)) {
                return !isArrayEqual(formValue, defaultValue);
            }

            return formValue !== defaultValue;
        });
    }, [formData]);

    const getWorkOrderMenuOptions = (dynamicFormAnswerId) => {

        const options = [
            messages["edit"],
            messages["delete"],
            messages["workOrder_trackerTab_changeStatus"],
        ]

        if (dynamicFormAnswerId) {
            options.push(messages['workOrder_trackerTab_viewAnswer'])
        }

        return options;
    };

    const handleClickWorkOrderMenu = (type, workOrder) => {
        if (type === messages["edit"]) {
            dispatch(dispatch(openModalCreateEditWorkOrder({ workOrderId: workOrder?.id })))
        } else if (type === messages["delete"]) {
            dispatch(openModalDeleteWorkOrder({ workOrder: workOrder }))
        } else if (type === messages["workOrder_trackerTab_changeStatus"]) {
            dispatch(openModalChangeStatusWorkOrder({ workOrder: workOrder }))
        } else if (type === messages['workOrder_trackerTab_viewAnswer']) {
            dispatch(openViewAnswersModal(workOrder?.dynamicFormAnswerId))
        }
    }

    //Columns
    const currentColumnsDate = useMemo(() => [
        {
            key: "driverName",
            title: messages['workOrder_customerTab_driver'],
            functionString: `
                function splitTextIntoLines(text, maxLength) {
                    let result = "";
                    for (let i = 0; i < text.length; i += maxLength) {
                        result += text.slice(i, i + maxLength) + "\\n";
                    }
                    return result.trim();
                };
                return row?.name ? splitTextIntoLines(row?.name, 30) : "";
            `,
            sort: true,
            sortField: "driverName",
            show: true,
            export: true,
            width: 250,
            exportValue: (row) => row?.name ? splitTextIntoLines(row.name, 30) : '',
            render: (row) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: 50 }}>
                            <Avatar name={row?.name} fileKey={row?.fileKey} />
                        </div>
                        <div style={{ wordWrap: 'break-word', width: 200 }}>
                            <span style={{ marginLeft: 5 }}>{row?.name}</span>
                        </div>
                    </div>
                )
            }
        },
        {
            key: "workOrderNumber",
            title: messages['workOrder_trackerTab_workOrderNumber'],
            functionString: 'return row?.workOrderNumber',
            sort: true,
            sortField: "workOrderNumber",
            show: true,
            export: true,
            width: 170,
            exportValue: (row) => row?.workOrderNumber,
            render: (row) => {
                return (
                    <Grid container alignItems="center">
                        <Grid item xs={8} style={{ display: 'flex', alignItems: 'center' }}>
                            <span>{row?.workOrderNumber}</span>
                        </Grid>
                        <Grid item xs={4} style={{ display: 'flex', alignItems: 'center' }}>
                            {
                                row?.dynamicFormAnswerId && (
                                    <IconButton
                                        tooltip={messages['workOrder_trackerTab_viewAnswer']}
                                        placement="bottom"
                                        variant="secondary"
                                        icon="feed_icon"
                                        size="small"
                                        onClick={() => {
                                            dispatch(openViewAnswersModal(row?.dynamicFormAnswerId))
                                        }}
                                    />
                                )
                            }
                            <MenuComponent
                                placement="right-start"
                                options={getWorkOrderMenuOptions(row?.dynamicFormAnswerId)}
                                icon="more_vert"
                                className="showInHover"
                                onClick={handleClickWorkOrderMenu}
                                value={row}
                            />
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "scheduledDate",
            title: messages['workOrder_trackerTab_scheduledDate'],
            functionString: `
                let format = "${format}" + " hh:mm a," + "${getAbbvTimezone(timeZone)}"

                return row?.scheduledDate ? moment(row?.scheduledDate).format(format) : ''
            `,
            sort: true,
            sortField: "scheduledDate",
            show: true,
            export: true,
            width: 190,
            exportValue: (row) => row?.scheduledDate,
            render: (row) => {
                if (!row?.scheduledDate) return <></>
                return (
                    <Typography style={{ fontSize: "0.875rem", fontWeight: 400 }}>
                        {moment(row?.scheduledDate).format(`${GetFormatForMoment(dateformat)} hh:mm a,`)}
                        <Tooltip title={timeZone} placement="bottom">
                            {` ${getAbbvTimezone(timeZone)}`}
                        </Tooltip>
                    </Typography>
                )
            }
        },
        {
            key: "customerName",
            title: messages['workOrder_trackerTab_customer'],
            functionString: `
                return row?.workOrderNumber ? 
                    "${messages['workOrder_trackerTab_customer_name']}: " + row?.customerName + "" +
                    "${messages['workOrder_trackerTab_contact_name']}: " + row?.contactName + ""
                : ""
            `,
            sort: true,
            sortField: "customerName",
            show: true,
            export: true,
            width: 250,
            exportValue: (row) => row?.customerName,
            render: (row) => (
                <div>
                    <p style={{ margin: 0 }}>
                        {`${messages['workOrder_trackerTab_customer_name']}: ${row?.customerName || ''}`}
                    </p>
                    <p style={{ margin: 0 }}>
                        {`${messages['workOrder_trackerTab_contact_name']}: ${row?.contactName || ''}`}
                    </p>
                </div>
            )
        },
        {
            key: "status_name",
            title: messages['workOrder_trackerTab_status'],
            functionString: 'return row?.statusName',
            sort: true,
            sortField: "statusName",
            show: true,
            export: true,
            exportValue: (row) => row?.statusName,
            render: (row) => (
                <Pill
                    size="dinamic"
                    item={{ id: row?.statusId, label: row?.statusName, color: `#${row?.statusColor}` }}
                />
            )
        },
        {
            key: "severity",
            title: messages['workOrder_trackerTab_severity'],
            functionString: 'return row?.severity',
            sort: true,
            sortField: "severity",
            show: true,
            export: true,
            width: 100,
            exportValue: (row) => {
                let severity = severityList.find((item) => item?.id === row?.severity)
                return severity?.name || row?.severity
            },
            render: (row) => {
                let severity = severityList.find((item) => item?.id === row?.severity)
                return (
                    <Pill
                        size="dinamic"
                        item={{ id: row?.id, label: severity?.name, color: `${severity?.color}` }}
                    />
                )
            }

        },
        {
            key: "statusUpdatedOn",
            title: messages['workOrder_trackerTab_statusUpdatedOn'],
            functionString: `
                let format = "${format}" + " hh:mm a, " + "${getAbbvTimezone(timeZone)}"

                return row?.statusUpdatedOn ? moment(row?.statusUpdatedOn).format(format) : ''
            `,
            sort: true,
            sortField: "statusUpdatedOn",
            show: true,
            export: true,
            width: 200,
            exportValue: (row) => row?.statusUpdatedOn,
            render: (row) => {
                if (!row?.statusUpdatedOn) return <></>
                return (
                    <Typography style={{ fontSize: "0.875rem", fontWeight: 400 }}>
                        {moment(row?.statusUpdatedOn).format(`${GetFormatForMoment(dateformat)} hh:mm a,`)}
                        <Tooltip title={timeZone} placement="bottom">
                            {` ${getAbbvTimezone(timeZone)}`}
                        </Tooltip>
                    </Typography>
                )
            }
        },
        {
            key: "customerLocation_address",
            title: messages['workOrder_trackerTab_address'],
            functionString: `
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.workOrderAddress)
                } catch (error) { }

                return currentAddress?.formatted || ""
            `,
            show: true,
            export: true,
            width: 250,
            exportValue: (row) => {
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.workOrderAddress)
                } catch (error) { }

                return currentAddress?.formatted || ""
            },
            render: (row) => {
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.workOrderAddress)
                } catch (error) { }

                return (
                    <Grid container alignItems="center">
                        {currentAddress?.formatted && (
                            <Grid
                                className="trackerTab-location-address"
                                item
                                onClick={() => {
                                    const data = {
                                        show: true,
                                        title: currentAddress.formatted,
                                        isPointerMarker: true,
                                        pointers: [
                                            {
                                                latitude: Number(row?.latitude),
                                                longitude: Number(row?.longitude),
                                                label: "Map Point",
                                                heading: 0,
                                                color: "000",
                                                eventName: "no event",
                                                draggable: false
                                            }
                                        ]
                                    };

                                    setDataToMap(data);
                                }}
                            >
                                <Icon className="iconFlag">room</Icon>
                                <p>{currentAddress.formatted || ""}</p>
                            </Grid>
                        )}
                    </Grid>
                )
            }
        },
        {
            key: "estimatedDurationMins",
            title: messages['workOrder_trackerTab_duration'],
            functionString: 'return row?.durationInMinutes',
            sort: true,
            sortField: "durationInMinutes",
            show: true,
            export: true,
            width: 80,
            exportValue: (row) => row?.durationInMinutes,
            render: (row) => <span>{row?.durationInMinutes}</span>
        },
    ], [messages, format, dateformat, timeZone, severityList, dispatch]);

    const currentColumnsDriver = useMemo(() => [
        {
            key: "workOrderNumber",
            title: messages['workOrder_trackerTab_workOrderNumber'],
            functionString: 'return row?.workOrderNumber',
            sort: true,
            sortField: "workOrderNumber",
            show: true,
            export: true,
            width: 180,
            exportValue: (row) => row?.workOrderNumber,
            render: (row) => {
                return (
                    <Grid container alignItems="center">
                        <Grid item xs={8} style={{ display: 'flex', alignItems: 'center' }}>
                            <span>{row?.workOrderNumber}</span>
                        </Grid>
                        <Grid item xs={4} style={{ display: 'flex', alignItems: 'center' }}>
                            {
                                row?.dynamicFormAnswerId && (
                                    <IconButton
                                        tooltip={messages['workOrder_trackerTab_viewAnswer']}
                                        placement="bottom"
                                        variant="secondary"
                                        icon="feed_icon"
                                        size="small"
                                        onClick={() => {
                                            dispatch(openViewAnswersModal(row?.dynamicFormAnswerId))
                                        }}
                                    />
                                )
                            }
                            <MenuComponent
                                placement="right-start"
                                options={getWorkOrderMenuOptions(row?.dynamicFormAnswerId)}
                                icon="more_vert"
                                className="showInHover"
                                onClick={handleClickWorkOrderMenu}
                                value={row}
                            />
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "scheduledDate",
            title: messages['workOrder_trackerTab_scheduledDate'],
            functionString: `
                let format = "${format}" + " hh:mm a, " + "${getAbbvTimezone(timeZone)}"

                return row?.scheduledDate ? moment(row?.scheduledDate).format(format) : ''
            `,
            sort: true,
            sortField: "scheduledDate",
            show: true,
            export: true,
            width: 190,
            exportValue: (row) => row?.scheduledDate,
            render: (row) => {
                if (!row?.scheduledDate) return <></>
                return (
                    <Typography style={{ fontSize: "0.875rem", fontWeight: 400 }}>
                        {moment(row?.scheduledDate).format(`${GetFormatForMoment(dateformat)} hh:mm a,`)}
                        <Tooltip title={timeZone} placement="bottom">
                            {` ${getAbbvTimezone(timeZone)}`}
                        </Tooltip>
                    </Typography>
                )
            }
        },
        {
            key: "customerName",
            title: messages['workOrder_trackerTab_customer'],
            functionString: `
                return row?.workOrderNumber ? 
                    "${messages['workOrder_trackerTab_customer_name']}: " + row?.customerName + "\n" +
                    "${messages['workOrder_trackerTab_contact_name']}: " + row?.contactName
                : ""
            `,
            sort: true,
            sortField: "customerName",
            show: true,
            export: true,
            width: 250,
            exportValue: (row) => row?.customerName,
            render: (row) => (
                <div>
                    <p style={{ margin: 0 }}>
                        {`${messages['workOrder_trackerTab_customer_name']}: ${row?.customerName || ''}`}
                    </p>
                    <p style={{ margin: 0 }}>
                        {`${messages['workOrder_trackerTab_contact_name']}: ${row?.contactName || ''}`}
                    </p>
                </div>
            )
        },
        {
            key: "status_name",
            title: messages['workOrder_trackerTab_status'],
            functionString: 'return row?.statusName',
            sort: true,
            sortField: "statusName",
            show: true,
            export: true,
            exportValue: (row) => row?.statusName,
            render: (row) => (
                <Pill
                    size="dinamic"
                    item={{ id: row?.statusId, label: row?.statusName, color: `#${row?.statusColor}` }}
                />
            )
        },
        {
            key: "statusUpdatedOn",
            title: messages['workOrder_trackerTab_statusUpdatedOn'],
            functionString: `
                let format = "${format}" + " hh:mm a, " + "${getAbbvTimezone(timeZone)}"

                return row?.statusUpdatedOn ? moment(row?.statusUpdatedOn).format(format) : ''
            `,
            sort: true,
            sortField: "statusUpdatedOn",
            show: true,
            export: true,
            width: 200,
            exportValue: (row) => row?.statusUpdatedOn,
            render: (row) => {
                if (!row?.statusUpdatedOn) return <></>
                return (
                    <Typography style={{ fontSize: "0.875rem", fontWeight: 400 }}>
                        {moment(row?.statusUpdatedOn).format(`${GetFormatForMoment(dateformat)} hh:mm a,`)}
                        <Tooltip title={timeZone} placement="bottom">
                            {` ${getAbbvTimezone(timeZone)}`}
                        </Tooltip>
                    </Typography>
                )
            }
        },
        {
            key: "customerLocation_address",
            title: messages['workOrder_trackerTab_address'],
            functionString: `
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.workOrderAddress)
                } catch (error) { }

                return currentAddress?.formatted || ""
            `,
            show: true,
            export: true,
            width: 250,
            exportValue: (row) => {
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.workOrderAddress)
                } catch (error) { }

                return currentAddress?.formatted || ""
            },
            render: (row) => {
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.workOrderAddress)
                } catch (error) { }

                return (
                    <Grid container alignItems="center">
                        {currentAddress?.formatted && (
                            <Grid
                                className="trackerTab-location-address"
                                item
                                onClick={() => {
                                    const data = {
                                        show: true,
                                        title: currentAddress.formatted,
                                        isPointerMarker: true,
                                        pointers: [
                                            {
                                                latitude: Number(row?.latitude),
                                                longitude: Number(row?.longitude),
                                                label: "Map Point",
                                                heading: 0,
                                                color: "000",
                                                eventName: "no event",
                                                draggable: false
                                            }
                                        ]
                                    };

                                    setDataToMap(data);
                                }}
                            >
                                <Icon className="iconFlag">room</Icon>
                                <p>{currentAddress.formatted || ""}</p>
                            </Grid>
                        )}
                    </Grid>
                )
            }
        },
        {
            key: "workOrderType_name",
            title: messages['workOrder_trackerTab_type'],
            functionString: 'return row?.workOrderTypeName',
            sort: true,
            sortField: "workOrderTypeName",
            show: true,
            export: true,
            width: 150,
            exportValue: (row) => row?.workOrderTypeName,
            render: (row) => <span>{row?.workOrderTypeName}</span>
        },
        {
            key: "estimatedDurationMins",
            title: messages['workOrder_trackerTab_duration'],
            functionString: 'return row?.durationInMinutes',
            sort: true,
            sortField: "durationInMinutes",
            show: true,
            export: true,
            width: 80,
            exportValue: (row) => row?.durationInMinutes,
            render: (row) => <span>{row?.durationInMinutes}</span>
        },
        {
            key: "severity",
            title: messages['workOrder_trackerTab_severity'],
            functionString: 'return row?.severity',
            sort: true,
            sortField: "severity",
            show: true,
            export: true,
            width: 100,
            exportValue: (row) => {
                let severity = severityList.find((item) => item?.id === row?.severity)
                return severity?.name || row?.severity
            },
            render: (row) => {
                let severity = severityList.find((item) => item?.id === row?.severity)
                return (
                    <Pill
                        size="dinamic"
                        item={{ id: row?.id, label: severity?.name, color: `${severity?.color}` }}
                    />
                )
            }

        }
    ], [messages, format, dateformat, timeZone, severityList, dispatch]);

    useEffect(() => {
        if (!paginationTrackerDay.columns.length && !paginationTrackerDriver.columns.length) {
            paginationTrackerDay.setColumns(currentColumnsDate);
            paginationTrackerDriver.setColumns(currentColumnsDriver);
            setSelectedItems([]);
            trackerTableRef?.current?.setPage(1);
            trackerTableRef?.current?.setSelectedItems([]);
        }
    }, [currentColumnsDate, currentColumnsDriver]);

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                messages["errorMessage"],
                messages["error"],
                3000,
                null,
                null,
                "warning"
            );
            dispatch(reset({ error: null }));
        }
        if (exportResult && (exportName === "tracker-day" || exportName === "tracker-driver")) {
            NotificationManager.info(
                messages["exportInProcessBody"],
                messages["exportInProcessTitle"],
                4000,
                null,
                null,
                "info",
                null
            );
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [error, exportResult, exportName]);


    useEffect(() => {
        return () => {
            // Clear timeout on unmount
            if (typingTimeRef.current) {
                clearTimeout(typingTimeRef.current);
            }
            dispatch(reset());
        };
    }, [])

    useEffect(() => {
        if ((preferences?.columnsDay?.length || preferences?.columnsDriver?.length) && !loadColumns) {

            // Handle columns for Day view
            if (preferences?.columnsDay?.length) {
                const preferenceMapDay = new Map(preferences.columnsDay.map(col => [col.key, col]));
                const reorderedColumnsDay = preferences.columnsDay
                    .map(pref => {
                        const col = currentColumnsDate.find(c => c.key === pref.key);
                        return col ? { ...col, show: pref.show } : null;
                    })
                    .filter(Boolean);
                const newColumnsDay = currentColumnsDate
                    .filter(col => !preferenceMapDay.has(col.key))
                    .map(col => ({ ...col, show: true }));
                const finalColumnsDay = [...reorderedColumnsDay, ...newColumnsDay];
                paginationTrackerDay.setColumns(finalColumnsDay);
            }

            // Handle columns for Driver view
            if (preferences?.columnsDriver?.length) {
                const preferenceMapDriver = new Map(preferences.columnsDriver.map(col => [col.key, col]));
                const reorderedColumnsDriver = preferences.columnsDriver
                    .map(pref => {
                        const col = currentColumnsDriver.find(c => c.key === pref.key);
                        return col ? { ...col, show: pref.show } : null;
                    })
                    .filter(Boolean);
                const newColumnsDriver = currentColumnsDriver
                    .filter(col => !preferenceMapDriver.has(col.key))
                    .map(col => ({ ...col, show: true }));
                const finalColumnsDriver = [...reorderedColumnsDriver, ...newColumnsDriver];
                paginationTrackerDriver.setColumns(finalColumnsDriver);
            }

            setPreferences({ ...preferences });
            setLoadColumns(true);
        }
    }, [preferences.columnsDay, preferences.columnsDriver, loadColumns])

    useEffect(() => {
        // Reset columns when locale, timezone or dateformat changes
        if ((paginationTrackerDay.columns.length > 0 || paginationTrackerDriver.columns.length > 0)) {
            // Reset both paginations with their respective columns
            paginationTrackerDay.setColumns(currentColumnsDate);
            paginationTrackerDriver.setColumns(currentColumnsDriver);

            // Update preferences with separated column preferences
            const updatedPreferences = {
                ...preferences,
                columnsDay: paginationTrackerDay.columns,
                columnsDriver: paginationTrackerDriver.columns
            };
            setPreferences(updatedPreferences);
            setLoadColumns(false);
        }
    }, [locale, timeZone, dateformat, currentColumnsDate, currentColumnsDriver]);

    const groupDriverContent = useCallback((item) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', minHeight: '48px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar name={item?.name} fileKey={item?.fileKey} width={48} />
                    <Typography noWrap >{item?.name}</Typography>
                </div>
                {item?.unitId ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <IconButton
                            style={{ color: 'black' }}
                            icon="gps_fixed"
                            tooltip={messages["unitOverViewPanel"]}
                            onClick={() => {
                                modelOverView(item?.unitId)
                            }} />
                        <Tooltip title={item?.unitName} placement="bottom">
                            <Typography noWrap >{item?.unitName}</Typography>
                        </Tooltip>
                    </div>
                ) : (
                    <Tooltip title={messages['workOrder_trackerTab_noUnit']} placement="bottom">
                        <WarningAmberIcon style={{ color: '#FFB53E' }} />
                    </Tooltip>
                )}
            </div>
        )
    }, [messages, modelOverView])

    // Generate headers based on current view columns
    const generateHeaders = useCallback(() => {
        const currentColumns = viewSelected === "day" ? currentColumnsDate : currentColumnsDriver;
        const headers = currentColumns
            .filter(col => col.export && col.show)
            .map(col => ({
                header: col.title,
                column: col.key,
                functionString: col.functionString || `return row?.${col.key}`
            }));

        if (viewSelected === "driver") {
            headers.unshift({
                header: messages["workOrder_trackerTab_driver"],
                column: "name",
                functionString: `return row?.workOrderNumber ? "" : row?.name`
            })
        } else {
            headers.unshift({
                header: messages["workOrder_trackerTab_date"],
                column: "days",
                functionString: `
                        return row?.days ? moment(row?.days).format("MMM DD, YYYY") : ''
                    `
            })
        }

        return headers

    }, [viewSelected, currentColumnsDate, currentColumnsDriver, messages]);

    const generateExportStyles = () => {
        return {
            0: {
                cellWidth: 60,
                fontSize: 11,
                fontStyle: "bold",
            }
        };
    };

    return (
        <Fragment>
            <Container maxWidth={false}>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Grid item xs={6} sm={2}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="center"
                            spacing={1}
                        >
                            <Grid item style={{ display: "flex" }}>
                                <IconButton
                                    tooltip={messages["filters"]}
                                    placement="bottom"
                                    icon="filter_list"
                                    size="small"
                                    disabled={true || loading}
                                    onClick={() => dispatch(openModalTrackerAdvancedFilters({ open: !openModalFilters }))}
                                    className={hasFilters() ? "trackerTab-filters-available" : ""}
                                />
                                <IconButton
                                    tooltip={messages["add"]}
                                    placement="bottom"
                                    variant="secondary"
                                    icon="add_box"
                                    size="small"
                                    disabled={loadingWorkOrders}
                                    onClick={() => {
                                        dispatch(openModalCreateEditWorkOrder())
                                    }}
                                />
                                {
                                    selectedItems?.length ? (
                                        <IconButton
                                            tooltip={messages["delete"]}
                                            placement="bottom"
                                            variant="secondary"
                                            icon="delete"
                                            size="small"
                                            onClick={() => {
                                                dispatch(openModalDeleteWorkOrder({ workOrderList: selectedItems }))
                                            }}
                                        />
                                    ) : <></>
                                }
                                <TableButtons
                                    {...paginationTracker}
                                    messages={messages}
                                    buttons={["export", "orderColumn"]}
                                    onExport={(type) => {
                                        dispatch(
                                            sendGenericExport(
                                                viewSelected === "day" ? "tracker-day" : "tracker-driver",
                                                type,
                                                searchWorkOrderGrouped.query,
                                                {
                                                    openSearch: paginationTracker?.searchText || "",
                                                    dateRange: {
                                                        startDate: formData?.rangeDate?.start || "",
                                                        endDate: formData?.rangeDate?.end || ""
                                                    },
                                                    driverIds: formData?.drivers?.map(item => item.driverId) || [],
                                                    statusIds: formData?.status?.map(item => item.id) || [],
                                                    severityIds: formData?.severities?.map(item => item.id) || [],
                                                    typeIds: formData?.types?.map(item => item.id) || [],
                                                    customerIds: formData?.customers?.map(item => item.id) || [],
                                                    tagIds: formData?.tags?.map(item => item.id) || [],
                                                    type: viewSelected,
                                                    allDrivers: formData?.drivers?.length > 0 ?
                                                        formData?.drivers.includes(-99) ? true : false
                                                        : formData?.tags?.length > 0 ? formData?.tags.includes(-99) ? true : false : true,
                                                    timezone: timeZone,
                                                    limit: paginationTracker?.pageSize || 10,
                                                    offset: (paginationTracker?.page - 1) * (paginationTracker?.pageSize || 10),
                                                    sort: paginationTracker?.sort || [{ field: "workOrderNumber", order: "ASC" }]
                                                },
                                                generateHeaders(),
                                                {
                                                    unit: 'px',
                                                    size: 'A4',
                                                    orientation: 'landscape',
                                                    fontSize: 10,
                                                },
                                                null,
                                                null,
                                                true,
                                                generateExportStyles()
                                            )
                                        );

                                    }}
                                    onChangeColumns={(columns) => {
                                        const preferenceKey = viewSelected === "day" ? "columnsDay" : "columnsDriver";
                                        savePreferences([{ key: preferenceKey, value: columns }])
                                        paginationTracker.setColumns(columns)
                                    }}
                                    columns={paginationTracker.columns}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item style={{ textAlign: "center", display: "flex" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Pill
                                style={{ padding: "0px 10px" }}
                                size="dinamic"
                                item={{ id: "day", label: messages["workOrder_trackerTab_date"], color: viewSelected === "day" ? "#005bf8" : "#F1F1F1" }}
                                onClick={() => setViewSelected("day")}
                            />
                            <Pill
                                style={{ padding: "0px 10px" }}
                                size="dinamic"
                                item={{ id: "driver", label: messages["workOrder_trackerTab_driver"], color: viewSelected === "driver" ? "#005bf8" : "#F1F1F1" }}
                                onClick={() => setViewSelected("driver")}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={4} className="mb-10 text-right">
                        <Tooltip title={messages["search_workOrder_trackerTab"]} placement="bottom" arrow>
                            <div>
                                <SearchInput
                                    placeholder={messages["search_workOrder_trackerTab"]}
                                    searchText={openSearch}
                                    onSearchText={(value) => applyChanges('_searchText', value)}
                                />
                            </div>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Preferences
                    preferencesKey={KEY_PREFERENCE}
                    onInitialPreferences={(data) => {
                        if (!loadPreference && timeZone && user) {
                            // Handle migration from old "columns" to new separated column preferences
                            let migrationData = { ...data };
                            if (data?.columns && !data?.columnsDay && !data?.columnsDriver) {
                                // Migrate old columns to both views (they'll be customized separately from now on)
                                migrationData.columnsDay = data.columns;
                                migrationData.columnsDriver = data.columns;
                                // Remove old columns key
                                delete migrationData.columns;
                            }

                            let updatedFormData = {
                                ...formData, ...['severities', 'status', 'types', 'rangeDate', 'timeFrame', 'drivers', 'customers', 'tags'].reduce((acc, key) => {
                                    if (migrationData?.[key]) acc[key] = migrationData[key];
                                    return acc;
                                }, {})
                            };
                            if (migrationData?.timeFrame) {
                                const timeFrame = getInitialRange(migrationData.timeFrame, migrationData?.rangeDate?.start, migrationData?.rangeDate?.end);
                                if (timeFrame?.start && timeFrame?.end) {
                                    updatedFormData = {
                                        ...updatedFormData,
                                        rangeDate: timeFrame,
                                        timeFrame: migrationData.timeFrame
                                    }
                                }
                            }

                            // Apply preferences and refresh table
                            setFormData(updatedFormData);
                            setPreferences(migrationData);
                            setLoadPreferences(true);

                            // Delay the refresh to ensure state has been updated
                            setTimeout(() => {
                                const currentPagination = viewSelected === "day" ? paginationTrackerDay : paginationTrackerDriver;
                                const currentConditions = buildConditions(updatedFormData, viewSelected, timeZone);
                                currentPagination.refresh({ _conditions: currentConditions });
                                setIsInitialLoadComplete(true);
                            }, 100);
                        }
                    }}
                >
                    <div style={loadingWorkOrdersGrouped ? {
                        visibility: "hidden"
                    } : {
                        visibility: "visible"
                    }}>
                        <div style={
                            loadingWorkOrdersGrouped ? {
                                margin: "15px 0px",
                                visibility: "visible",
                                padding: "25px 0px",
                                borderTop: "1px solid #e0e0e0",

                            } : {
                                visibility: "hidden"
                            }}>
                            <LinearProgress />
                        </div>
                        {

                            viewSelected === "day" ? (
                                <TableView
                                    {...paginationTracker}
                                    keyColumn="id"
                                    loading={loadingWorkOrdersGrouped}
                                    topheight={260}
                                    grouping
                                    groupingValueToCompare={"driver"}
                                    data={workOrdersGrouped.map((item) => ({
                                        ...item,
                                        type: item?.recordType === 'GROUP' ? 'group' : '',
                                        content: (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', minHeight: '48px' }}>
                                                <Typography style={{ fontSize: "0.9rem", fontWeight: 900 }}>
                                                    {moment.utc(item?.days).format("MMM DD, YYYY")}
                                                    <Tooltip title={timeZone} placement="bottom">
                                                        {` ${getAbbvTimezone(timeZone)}`}
                                                    </Tooltip>
                                                </Typography>
                                            </div>
                                        )
                                    })) || []}
                                    total={totalWorkOrdersGrouped}
                                    exportOrientation="landscape"
                                    onSelectedItems={(items) => {
                                        setSelectedItems(items)
                                    }}
                                    onChangeSort={(value) => applyChanges('_sort', value)}
                                    onChangeRowsPerPage={(value) => applyChanges('_pageSize', value)}
                                    onChangePage={(value) => applyChanges('_page', value)}
                                    onChangeColumns={(columns) => {
                                        let currentColumns = paginationTracker?.columns || []
                                        let finalsColumns = completeColumns(currentColumns, columns)
                                        savePreferences([{ key: "columnsDay", value: finalsColumns }])
                                        paginationTracker.setColumns(finalsColumns)
                                    }}
                                    hideAllInPagination
                                />
                            ) : (
                                <TableView
                                    {...paginationTracker}
                                    keyColumn="id"
                                    loading={loadingWorkOrdersGrouped}
                                    topheight={260}
                                    data={workOrdersGrouped.map((item) => ({
                                        ...item,
                                        type: item?.recordType === 'GROUP' ? 'group' : '',
                                        content: groupDriverContent(item)
                                    })) || []}
                                    total={totalWorkOrdersGrouped}
                                    exportOrientation="landscape"
                                    onSelectedItems={(items) => {
                                        setSelectedItems(items)
                                    }}
                                    onChangeSort={(value) => applyChanges('_sort', value)}
                                    onChangeRowsPerPage={(value) => applyChanges('_pageSize', value)}
                                    onChangePage={(value) => applyChanges('_page', value)}
                                    onChangeColumns={(columns) => {
                                        let currentColumns = paginationTracker?.columns || []
                                        let finalsColumns = completeColumns(currentColumns, columns)
                                        savePreferences([{ key: "columnsDriver", value: finalsColumns }])
                                        paginationTracker.setColumns(finalsColumns)
                                    }}
                                    hideAllInPagination
                                />
                            )
                        }
                    </div>
                    <AdvancedFilters
                        tableRef={paginationTracker}
                        formData={formData}
                        setFormData={setFormData}
                        initialState={INITIAL_STATE}
                        applyChanges={applyChanges}
                        hasFilters={hasFilters}
                        savePreferences={savePreferences}
                        paginationTracker={paginationTracker}
                        viewSelected={viewSelected}
                        buildConditions={buildConditions}
                    />
                </Preferences>
            </Container>
            {openModalCreateEdit && <CreateEditWorkOrder refresh={() => refreshTable(true)} />}
            {openModalDelete && <DeleteWorkOrder refresh={() => refreshTable(true)} />}
            {openModalChangeStatus && <ChangeStatus refresh={() => refreshTable(false)} />}
            {openModalViewAnswers && <ViewAnswers />}
            {
                dataToMap?.show &&
                <MapModal
                    hiddenButton
                    {...dataToMap}
                    hiddenTraffic={true}
                    hiddenWeather={true}
                    hiddenSearchMap={true}
                    hideModal={() => {
                        setDataToMap({ show: false });
                    }}
                    zoom={18}
                />
            }
        </Fragment>
    );
};

const buildConditions = (formData, viewSelected, timeZone) => {
    // Ensure we have valid defaults
    const safeFormData = formData || {};
    const safeViewSelected = viewSelected || "day";
    const safeTimeZone = timeZone || "UTC";

    const conditions = {};

    if (safeFormData?.status?.length > 0) {
        conditions.status = safeFormData.status.map(item => item.id);
    }
    if (safeFormData?.severities?.length > 0) {
        conditions.severities = safeFormData.severities.map(item => item.id);
    }
    if (safeFormData?.types?.length > 0) {
        conditions.types = safeFormData.types.map(item => item.id);
    }
    if (safeFormData?.drivers?.length > 0) {
        conditions.drivers = safeFormData.drivers.map(item => item.driverId);
    }
    if (safeFormData?.customers?.length > 0) {
        conditions.customers = safeFormData.customers.map(item => item.id);
    }
    if (safeFormData?.tags?.length > 0) {
        conditions.tags = safeFormData.tags.map(item => item.id);
    }
    if (safeFormData?.forceRefreshMemcached) {
        conditions.forceRefreshMemcached = safeFormData.forceRefreshMemcached;
    }

    return [{
        type: safeViewSelected,
        range: {
            start: safeFormData.rangeDate?.start || moment().startOf('month'),
            end: safeFormData.rangeDate?.end || moment().endOf('month')
        },
        statusIds: conditions.status || [],
        severityIds: conditions.severities || [],
        typeIds: conditions.types || [],
        driverIds: conditions.drivers || [],
        customerIds: conditions.customers || [],
        tagIds: conditions.tags || [],
        timeZone: safeTimeZone,
        forceRefreshMemcached: safeFormData?.forceRefreshMemcached || false
    }];
};

export default TrackerTab;
